import { useEffect, useState, useMemo, useCallback } from "react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CaretLeft, CaretRight, Calendar as CalendarIcon, User as UserIcon, PencilSimple, Trash, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_STYLES = {
  present:  { bg: "bg-[#16A34A]", text: "text-white", label: "Present" },
  absent:   { bg: "bg-[#FF2400]", text: "text-white", label: "Absent" },
  off:      { bg: "bg-gray-200", text: "text-gray-600", label: "Weekly off" },
  holiday:  { bg: "bg-[#F59E0B]", text: "text-white", label: "Holiday" },
  leave:    { bg: "bg-[#0047AB]", text: "text-white", label: "Leave" },
  half_day: { bg: "bg-[#8B5CF6]", text: "text-white", label: "Half day" },
  upcoming: { bg: "bg-white border border-dashed border-gray-300", text: "text-gray-400", label: "Upcoming" },
};

const OVERRIDE_STATUSES = [
  { value: "present", label: "Present" },
  { value: "absent", label: "Absent" },
  { value: "half_day", label: "Half day" },
  { value: "leave", label: "Leave" },
  { value: "holiday", label: "Holiday" },
  { value: "off", label: "Off" },
];

const fmtHrs = (sec) => {
  if (!sec) return "";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isHr = isAdmin || user?.hr_access === true;
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(user?.id || "");
  const [data, setData] = useState(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ date: "", status: "present", note: "", override_id: null });

  useEffect(() => {
    if (isHr) {
      api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active)));
    }
  }, [isHr]);

  const load = useCallback(async () => {
    if (!selectedUserId) return;
    try {
      const { data } = await api.get("/attendance", {
        params: { year, month, user_id: isHr ? selectedUserId : undefined },
      });
      setData(data);
    } catch (e) { toast.error(errorMessage(e)); }
  }, [year, month, selectedUserId, isHr]);

  useEffect(() => { load(); }, [load]);

  const openOverride = (day) => {
    if (!isHr) return;
    setOverrideForm({
      date: day.date,
      status: day.status === "upcoming" ? "present" : day.status,
      note: day.override_note || "",
      is_override: !!day.override,
    });
    setOverrideOpen(true);
  };

  const saveOverride = async () => {
    try {
      await api.post("/attendance/overrides", {
        user_id: selectedUserId,
        date: overrideForm.date,
        status: overrideForm.status,
        note: overrideForm.note || null,
      });
      toast.success("Attendance updated");
      setOverrideOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const clearOverride = async () => {
    try {
      // Fetch the override id
      const { data: overrides } = await api.get("/attendance/overrides", {
        params: { user_id: selectedUserId, month: `${year}-${String(month).padStart(2, "0")}` },
      });
      const target = overrides.find((o) => o.date === overrideForm.date);
      if (target) {
        await api.delete(`/attendance/overrides/${target.id}`);
        toast.success("Override cleared");
      }
      setOverrideOpen(false);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  // Compute leading blanks (based on Monday-first weekday of day 1)
  const grid = useMemo(() => {
    if (!data) return { blanks: 0, days: [] };
    const first = new Date(Date.UTC(year, month - 1, 1));
    // getUTCDay: Sun=0..Sat=6 → we want Mon-first: (day + 6) % 7
    const blanks = (first.getUTCDay() + 6) % 7;
    return { blanks, days: data.days };
  }, [data, year, month]);

  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Attendance</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
            {isHr ? "Team Attendance" : "My Attendance"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Present = went online any time that day. Weekly-offs come from the employee&apos;s shift.
            {isHr && <span className="ml-1 text-[#0047AB] font-medium">Click any day to override attendance.</span>}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {isHr && users.length > 0 && (
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="h-9 min-w-[200px]" data-testid="attendance-user-select">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="inline-flex items-center border border-gray-200 rounded-sm h-9 overflow-hidden">
            <Button variant="ghost" size="icon" onClick={prevMonth} className="h-9 w-9 rounded-none" data-testid="attendance-prev-month">
              <CaretLeft size={14} weight="bold" />
            </Button>
            <div className="px-4 text-sm font-bold tracking-tight" data-testid="attendance-month-label">
              {MONTHS[month - 1]} {year}
            </div>
            <Button variant="ghost" size="icon" onClick={nextMonth} className="h-9 w-9 rounded-none" data-testid="attendance-next-month">
              <CaretRight size={14} weight="bold" />
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }}
            className="h-9 rounded-sm"
            data-testid="attendance-today-button"
          >
            Today
          </Button>
          {isHr && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/attendance/report.xlsx?year=${year}&month=${month}`;
                  const token = localStorage.getItem("token");
                  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                  if (!res.ok) throw new Error("Download failed");
                  const blob = await res.blob();
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `attendance_${year}_${String(month).padStart(2, "0")}.xlsx`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                } catch (e) { toast.error("Download failed"); }
              }}
              className="h-9 rounded-sm border-[#16A34A] text-[#16A34A] hover:bg-green-50"
              data-testid="attendance-download-button"
            >
              <DownloadSimple size={14} weight="bold" className="mr-1" /> Download report
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="attendance-summary">
        <StatBlock label="Present" value={summary.present || 0} tone="bg-green-50 border-[#16A34A] text-[#16A34A]" />
        <StatBlock label="Absent" value={summary.absent || 0} tone="bg-red-50 border-[#FF2400] text-[#FF2400]" />
        <StatBlock label="Leave" value={summary.leave || 0} tone="bg-blue-50 border-[#0047AB] text-[#0047AB]" />
        <StatBlock label="Holiday" value={summary.holiday || 0} tone="bg-amber-50 border-[#F59E0B] text-[#B45309]" />
        <StatBlock label="Weekly off" value={summary.off || 0} tone="bg-gray-50 border-gray-300 text-gray-600" />
        <StatBlock label="Online hrs" value={fmtHrs(summary.total_online_sec || 0) || "0"} tone="bg-white border-gray-300 text-gray-800" />
      </div>

      {/* Calendar */}
      <div className="border border-gray-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50/60">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-gray-500 text-center">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7" data-testid="attendance-calendar">
          {Array.from({ length: grid.blanks }).map((_, i) => (
            <div key={`blank-${i}`} className="min-h-[92px] border-b border-r border-gray-100 bg-gray-50/40" />
          ))}
          {grid.days.map((d) => {
            const style = STATUS_STYLES[d.status] || STATUS_STYLES.upcoming;
            const dayNum = parseInt(d.date.split("-")[2], 10);
            const isToday = d.date === new Date().toISOString().slice(0, 10);
            return (
              <div
                key={d.date}
                className={`min-h-[92px] border-b border-r border-gray-100 p-2 flex flex-col ${style.bg} ${style.text} relative ${isHr ? "cursor-pointer hover:brightness-95" : ""}`}
                title={`${d.date} · ${style.label}${d.holiday_name ? " · " + d.holiday_name : ""}${d.leave_type ? " · " + d.leave_type : ""}${d.online_sec ? " · " + fmtHrs(d.online_sec) : ""}${d.override ? " · OVERRIDE" : ""}`}
                data-testid={`attendance-day-${d.date}`}
                onClick={() => isHr && openOverride(d)}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-display font-black text-lg leading-none ${isToday ? "underline decoration-2 underline-offset-2" : ""}`}>{dayNum}</span>
                  <span className="text-[9px] uppercase tracking-wider opacity-80">{d.override ? "★ " : ""}{style.label}</span>
                </div>
                {d.status === "present" && d.online_sec > 0 && (
                  <span className="text-[10px] font-mono mt-auto self-end opacity-90">{fmtHrs(d.online_sec)}</span>
                )}
                {d.status === "holiday" && d.holiday_name && (
                  <span className="text-[10px] mt-auto opacity-95 line-clamp-2">{d.holiday_name}</span>
                )}
                {d.status === "leave" && d.leave_type && (
                  <span className="text-[10px] uppercase tracking-wider mt-auto opacity-95">{d.leave_type}</span>
                )}
                {d.status === "half_day" && (
                  <span className="text-[10px] uppercase tracking-wider mt-auto opacity-95">
                    ½ {d.half_day || ""}{d.leave_type ? ` · ${d.leave_type}` : ""}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        {Object.entries(STATUS_STYLES).map(([k, s]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${s.bg}`} />
            {s.label}
          </span>
        ))}
      </div>

      {isHr && <MonthlyReport year={year} month={month} />}

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <PencilSimple size={16} weight="duotone" className="text-[#0047AB]" /> Override attendance
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Manually set attendance for <b className="font-mono">{overrideForm.date}</b>. This overrides the auto-computed status.
          </p>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={overrideForm.status} onValueChange={(v) => setOverrideForm({ ...overrideForm, status: v })}>
                <SelectTrigger data-testid="override-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OVERRIDE_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                rows={2}
                value={overrideForm.note}
                onChange={(e) => setOverrideForm({ ...overrideForm, note: e.target.value })}
                placeholder="e.g. Client site visit, WFH approved, etc."
                data-testid="override-note-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {overrideForm.is_override && (
              <Button variant="outline" onClick={clearOverride} className="border-[#FF2400] text-[#FF2400] hover:bg-red-50 rounded-sm" data-testid="override-clear-button">
                <Trash size={12} weight="bold" className="mr-1" /> Clear override
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setOverrideOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveOverride} className="bg-[#0047AB] hover:bg-[#0033A0] text-white rounded-sm" data-testid="override-save-button">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBlock({ label, value, tone }) {
  return (
    <div className={`border rounded-sm px-4 py-3 ${tone}`}>
      <p className="text-[11px] uppercase tracking-wider font-bold opacity-80">{label}</p>
      <p className="font-display text-2xl font-black tracking-tight leading-tight">{value}</p>
    </div>
  );
}

function MonthlyReport({ year, month }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/attendance/report", { params: { year, month } }).then((r) => setRows(r.data.rows || []));
  }, [year, month]);
  return (
    <div className="border border-gray-200 rounded-sm overflow-hidden" data-testid="attendance-report">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/60 flex items-center gap-2">
        <CalendarIcon size={16} weight="duotone" className="text-[#0047AB]" />
        <h2 className="font-display font-bold tracking-tight">Monthly report — {MONTHS[month - 1]} {year}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-white">
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Employee</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Role</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Present</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Absent</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Leave</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Holiday</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Off</th>
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold text-gray-500">Online hrs</th>
            </tr>
          </thead>
          <tbody data-testid="attendance-report-rows">
            {rows.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500 text-sm">No data</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium flex items-center gap-2">
                  <UserIcon size={14} weight="duotone" className="text-gray-400" />
                  {r.name}
                </td>
                <td className="px-4 py-3 text-xs uppercase tracking-wider text-gray-500">{r.role}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#16A34A] font-bold">{r.present}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#FF2400]">{r.absent}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#0047AB]">{r.leave}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.holiday}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.off}</td>
                <td className="px-4 py-3 font-mono text-xs">{fmtHrs(r.total_online_sec) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
